import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Typography } from "@mui/material";
import { FALLBACK_DASHBOARD } from "../data/biomarkers";
import { getMyResults, getSampleResults } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";
import FallbackNotice from "../components/FallbackNotice";
import { Card, Cards, Section } from "../components/ui";
import { useAuth0 } from "../hooks/useAuth0";
import { fonts, tokens } from "../theme";

const STATUS = {
  in: { label: "In range", color: tokens.inRange },
  watch: { label: "Watch", color: tokens.watch },
  out: { label: "Out of range", color: tokens.outRange },
};

function RangeBar({ r }) {
  const span = r.high * 1.25 - r.low * 0.75 || 1;
  const pct = (v) => Math.min(100, Math.max(0, ((v - r.low * 0.75) / span) * 100));
  return (
    <Box sx={{ position: "relative", height: 10, borderRadius: 5, background: tokens.line, mt: 2 }} aria-hidden>
      <Box sx={{ position: "absolute", left: `${pct(r.low)}%`, width: `${pct(r.high) - pct(r.low)}%`, top: 0, bottom: 0, background: tokens.sage, borderRadius: 5 }} />
      <Box sx={{ position: "absolute", left: `calc(${pct(r.value)}% - 7px)`, top: -3, width: 16, height: 16, borderRadius: "50%", background: STATUS[r.status].color, border: "3px solid #fff", boxShadow: "0 1px 4px rgba(0,0,0,.3)" }} />
    </Box>
  );
}

function Spark({ data, color }) {
  const w = 96, h = 30;
  const min = Math.min(...data), max = Math.max(...data), d = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - 4 - ((v - min) / d) * (h - 8)}`).join(" ");
  return (
    <svg width={w} height={h} role="img" aria-label="Trend over the last four visits">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Dashboard({ sample = false }) {
  const { user } = useAuth0();
  const [filter, setFilter] = useState("all");
  // The public sample page may fall back to built-in data; a real person's results never do.
  const { data, loading, error, usingFallback } = useApiData(
    sample ? getSampleResults : getMyResults,
    sample ? FALLBACK_DASHBOARD : null
  );

  if (loading) {
    return <Section tone="cream"><Box sx={{ display: "grid", placeItems: "center", py: 10 }}><CircularProgress /></Box></Section>;
  }
  if (error || !data) {
    return <Section tone="cream" narrow><Alert severity="error">We could not load your results right now. Please try again in a moment.</Alert></Section>;
  }

  const markers = data.markers || [];
  const plan = data.plan || [];
  const counts = data.counts || { in: 0, watch: 0, out: 0 };
  const rows = markers.filter((r) => filter === "all" || r.status === filter);

  return (
    <Section tone="cream" sx={{ py: { xs: 5, md: 8 } }}>
      <FallbackNotice show={usingFallback} />
      {sample && (
        <Alert severity="info" sx={{ mb: 4 }}>
          Sample data for the preview build. Have an account? <RouterLink to="/login">Log in</RouterLink>.
        </Alert>
      )}
      <Typography variant="h1" sx={{ fontSize: { xs: 34, md: 48 } }}>
        {sample || !user?.name ? "Your results" : `Welcome, ${user.name.split(" ")[0]}`}
      </Typography>

      {!data.hasResults ? (
        <Alert severity="info" sx={{ mt: 4 }}>
          No results yet. Once your first visit is processed they will appear here.{" "}
          <RouterLink to="/sample-dashboard">See a sample dashboard</RouterLink>.
        </Alert>
      ) : (
        <>
          <Typography sx={{ color: tokens.muted, mt: 1 }}>
            {data.visitCount} {data.visitCount === 1 ? "visit" : "visits"} · clinician summary available
          </Typography>

          <Cards min={200} sx={{ mt: 4 }}>
            {Object.entries(STATUS).map(([k, s]) => (
              <Card key={k} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: "50%", background: s.color }} />
                <Box><Typography sx={{ fontSize: 32, fontFamily: fonts.display, lineHeight: 1 }}>{counts[k]}</Typography><Typography sx={{ color: tokens.muted, fontSize: 14 }}>{s.label}</Typography></Box>
              </Card>
            ))}
          </Cards>

          <Box sx={{ mt: 5, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {[["all", "All"], ...Object.entries(STATUS).map(([k, s]) => [k, s.label])].map(([k, label]) => (
              <Chip key={k} label={label} clickable onClick={() => setFilter(k)} sx={{ background: filter === k ? tokens.forest : "#fff", color: filter === k ? "#fff" : tokens.ink, border: `1px solid ${tokens.line}` }} />
            ))}
          </Box>

          <Cards min={300} sx={{ mt: 3 }}>
            {rows.map((r) => (
              <Card key={r.id}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, color: tokens.muted }}>{r.category}</Typography>
                    <Typography variant="h4" sx={{ fontSize: 20 }}>{r.name}</Typography>
                  </Box>
                  <Chip size="small" label={STATUS[r.status].label} sx={{ background: STATUS[r.status].color, color: "#fff", fontWeight: 600 }} />
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mt: 2 }}>
                  <Typography sx={{ fontFamily: fonts.display, fontSize: 34, lineHeight: 1 }}>{r.value}<Typography component="span" sx={{ fontSize: 14, color: tokens.muted, ml: .6 }}>{r.unit}</Typography></Typography>
                  {r.history.length > 1 && <Spark data={r.history} color={STATUS[r.status].color} />}
                </Box>
                <RangeBar r={r} />
                <Typography sx={{ mt: 1.5, fontSize: 12.5, color: tokens.muted }}>Reference range {r.low} to {r.high} {r.unit}</Typography>
              </Card>
            ))}
          </Cards>

          {plan.length > 0 && (
            <>
              <Typography variant="h2" sx={{ fontSize: 32, mt: 8, mb: 3 }}>Your plan</Typography>
              <Cards min={280}>
                {plan.map((p) => (
                  <Card key={p.title} sx={{ background: tokens.sage, border: "none" }}>
                    <Typography variant="h4" sx={{ fontSize: 20 }}>{p.title}</Typography>
                    <Typography sx={{ mt: 1, color: tokens.ink, opacity: .8 }}>{p.body}</Typography>
                  </Card>
                ))}
              </Cards>
            </>
          )}
        </>
      )}
      <Button sx={{ mt: 5 }} variant="outlined" component={RouterLink} to="/shop">Add a test</Button>
    </Section>
  );
}
